import React, {
  useCallback,
  useContext,
  useLayoutEffect,
  useState,
} from 'react';
import styles from './index.module.scss';
import { Collapse, Descriptions, Divider, Flex, Alert } from 'antd';
import AccionaButton from '../../../../../components/AccionaButton';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { getConcreteClassesStatistical } from '../../../../../services/api/concreteClasses';
import UncontrolledAccionaTable from '../../../../../components/AccionaTable/UncontrolledTable';
import { getColumns } from './columns';
import AccionaTableButton from '../../../../../components/AccionaTable/AccionaTableButton';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  DownOutlined,
} from '@ant-design/icons';
import { theme } from 'antd';
import PropTypes from '../../../../../utils/propTypes';
import useNormativeApi from '../CustomTraceabilityList/components/EditModal/hooks/useNormativeApi';
import { MainContext } from '../../../../../context/MainContext';
import { NotificationsContext } from '../../../../../context/NotificationsContext';

const { actionsWrapper } = styles;

const CustomLottingList = ({ normative }) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const { project } = useOutletContext();
  const { setLoading } = useContext(MainContext);
  const { pushNotification } = useContext(NotificationsContext);
  const { id, class: concreteClassParam } = useParams();
  const [concreteClass, setConcreteClass] = useState();
  const [casesList, setCasesList] = useState();
  const { token } = theme.useToken();
  const { updateDataScript } = useNormativeApi();

  const getIcon = useCallback(
    (value) => {
      if (value === true || value === 1)
        return <CheckCircleFilled style={{ color: token.colorSuccess }} />;

      if (value === false || value === 0)
        return <CloseCircleFilled style={{ color: token.colorError }} />;

      return (
        <span style={{ color: 'rgb(102, 102, 102)' }}>
          {intl
            .formatMessage({
              id: 'pages.quality.normative.statistical-control.batchDetail.notEvaluated',
              defaultMessage: 'NO EVALUADO',
            })
            .toLocaleUpperCase()}
        </span>
      );
    },
    [token.colorSuccess, token.colorError, intl],
  );

  const getResistenceCompilance = useCallback(
    (d) => {
      if (
        d.delivery_note_data?.[0]?.concrete_quality ||
        d.delivery_note_data?.[0]?.certified_dis
      ) {
        return (
          <>
            {getIcon(
              d.compliance === null
                ? null
                : d.compliance === false
                ? d.compliance90
                : d.compliance,
            )}
          </>
        );
      }

      return d.compliance !== null
        ? getIcon(d.compliance)
        : intl
            .formatMessage({
              id: 'notEvaluated',
            })
            .toUpperCase();
    },
    [getIcon, intl],
  );

  const renderMinMashesCompliance = useCallback(
    (lot) => {
      const nControlMash = lot.n_control_mash || 0;
      const minNControlMash = lot.min_n_control_mash || 0;

      let icon = null;
      let color = '';

      if (nControlMash >= minNControlMash) {
        icon = <CheckCircleFilled style={{ color: token.colorSuccess }} />;
        color = token.colorSuccess;
      } else {
        icon = <CheckCircleFilled style={{ color: token.colorWarning }} />;
        color = token.colorWarning;
      }

      return (
        <div className={styles.complianceContainer}>
          <div className={styles.complianceColumn}>
            <span className={styles.complianceLabel}>
              {intl.formatMessage({
                id: 'pages.quality.normative.statistical-control.lotting.list.minimum',
                defaultMessage: 'Mínimas',
              })}
            </span>
            <div className={styles.complianceValue}>
              <span>{minNControlMash}</span>
            </div>
          </div>
          <Divider className={styles.divider} type="vertical" />
          <div className={styles.complianceColumn}>
            <span className={styles.complianceLabel}>
              {intl.formatMessage({
                id: 'pages.quality.normative.statistical-control.lotting.list.controlled',
                defaultMessage: 'Controladas',
              })}
            </span>
            <div className={styles.complianceValue}>
              <span>{nControlMash}</span>
            </div>
          </div>
          <Divider className={styles.divider} type="vertical" />
          <div className={styles.complianceColumn}>
            <div className={styles.complianceValue}>
              <span className={styles.complianceIcon} style={{ color }}>
                {icon}
              </span>
            </div>
          </div>
        </div>
      );
    },
    [token, intl],
  );

  const actions = useCallback(
    (item, caseNumber) => {
      return (
        <>
          <AccionaTableButton
            type="view"
            toolTip={intl.formatMessage({
              id: 'pages.quality.normative.customTypeControlList.list.view.tooltip',
            })}
            onClick={() => {
              localStorage.setItem('Caso', caseNumber.toString());
              localStorage.setItem('Lote', item.batch_code.toString());
              localStorage.setItem(
                'CBID',
                item.delivery_note_data[0].concrete_batch.toString(),
              );
              setLoading(true);
              updateDataScript(
                true,
                item.delivery_note_data[0].id,
                item.delivery_note_data[0].concrete_batch,
                item.delivery_note_data[0].concrete_batch,
              )
                .then(() => {
                  pushNotification({
                    message: intl.formatMessage({
                      id: 'pages.quality.normative.statistical-control.lotting.list.success.updatingDataScript',
                    }),
                    type: 'success',
                  });
                  navigate(
                    String(
                      `batch/${item.rawData.batch_code}/case/${caseNumber}`,
                    ),
                  );
                })
                .catch(() => {
                  setLoading(false);
                  pushNotification({
                    message: intl.formatMessage({
                      id: 'pages.quality.normative.statistical-control.lotting.list.error.updatingDataScript',
                    }),
                    type: 'error',
                  });
                })
                .finally(() => {
                  setLoading(false);
                });
            }}
          />
        </>
      );
    },
    [intl, navigate, pushNotification, setLoading, updateDataScript],
  );

  const getLotificationList = useCallback(
    (batch_data) => {
      let cont1 = 0;
      let cont2 = 0;
      const data = [...batch_data];
      const case0Batches = [];
      const case1Batches = [];
      const case2Batches = [];
      const case3Batches = [];
      let totalCase0Batches = 0;
      let totalCase1Batches = 0;
      let totalCase2Batches = 0;
      let totalCase3Batches = 0;
      let case2Error = false;
      let case3Error = false;
      data.sort((a, b) => {
        if (a.delivery_note_data === null) {
          a.delivery_note_data[0]['mash_code'] = Number.MAX_VALUE;
        }
        if (b.delivery_note_data === null) {
          b.delivery_note_data[0]['mash_code'] = Number.MAX_VALUE;
        }
        if (
          a.delivery_note_data[0]['mash_code'] >
          b.delivery_note_data[0]['mash_code']
        ) {
          return 1;
        }
        if (
          a.delivery_note_data[0]['mash_code'] <
          b.delivery_note_data[0]['mash_code']
        ) {
          return -1;
        }
        return 0;
      });
      data.forEach((concreteBatch) => {
        if (concreteBatch.delivery_note_data[0].concrete_quality === true) {
          case0Batches.push(concreteBatch);
          totalCase0Batches =
            totalCase0Batches +
            concreteBatch.delivery_note_data.filter(
              (a) => a.mash_control === true,
            ).length;
        } else if (concreteBatch.delivery_note_data[0].certified_dis === true) {
          case1Batches.push(concreteBatch);
          totalCase1Batches =
            totalCase1Batches +
            concreteBatch.delivery_note_data.filter(
              (a) => a.mash_control === true,
            ).length;
        } else {
          if (cont1 < 36) {
            cont1 =
              cont1 +
              concreteBatch.delivery_note_data.filter(
                (a) => a.mash_control === true,
              ).length;
            // Para EHE: esto es caso 2, para COE: esto es caso 3
            if (normative === 'ehe') {
              case2Batches.push(concreteBatch);
              totalCase2Batches =
                totalCase2Batches + concreteBatch.n_control_mash;
              if (
                concreteBatch.min_n_control_mash === null &&
                concreteBatch.n_control_mash === null
              ) {
                case2Error = true;
              }
              if (
                concreteBatch.min_n_control_mash > concreteBatch.n_control_mash
              ) {
                case2Error = true;
              }
            } else {
              case3Batches.push(concreteBatch);
              totalCase3Batches =
                totalCase3Batches + concreteBatch.n_control_mash;
              if (
                concreteBatch.min_n_control_mash === null &&
                concreteBatch.n_control_mash === null
              ) {
                case3Error = true;
              }
              if (
                concreteBatch.min_n_control_mash > concreteBatch.n_control_mash
              ) {
                case3Error = true;
              }
            }
          } else {
            // Para EHE: esto es caso 3, para COE: esto es caso 2
            if (normative === 'ehe') {
              case3Batches.push(concreteBatch);
              cont2 = cont2 + concreteBatch.n_control_mash;
              totalCase3Batches = totalCase2Batches + cont2;
              if (
                concreteBatch.min_n_control_mash >
                  concreteBatch.n_control_mash &&
                case2Error === false
              ) {
                case3Error = true;
              }
            } else {
              case2Batches.push(concreteBatch);
              cont2 = cont2 + concreteBatch.n_control_mash;
              totalCase2Batches = totalCase3Batches + cont2;
              if (
                concreteBatch.min_n_control_mash >
                  concreteBatch.n_control_mash &&
                case3Error === false
              ) {
                case2Error = true;
              }
            }
          }
        }
      });

      setCasesList({
        case0: {
          batches: case0Batches,
          totalBatches: totalCase0Batches,
        },
        case1: {
          batches: case1Batches,
          totalBatches: totalCase1Batches,
        },
        case2: {
          batches: case2Batches,
          totalBatches: totalCase2Batches,
          error: case2Error,
        },
        case3: {
          batches: case3Batches,
          totalBatches: totalCase3Batches,
          error: case3Error,
        },
      });
    },
    [normative],
  );

  const commonProps = {
    columns: getColumns(intl),
    showPagination: false,
    rowHeight: '50px',
    dataParseFunction: (data) => {
      return data.map((d) => ({
        ...d,
        batch_code:
          project.version === 1
            ? d.batch_code
            : `${d.tag_batch_data?.tag_data.name} ${d.tag_batch_data?.tag_number}`,
        batch_volume: (
          <span
            style={{
              color: !d.batch_volume_compliance ? token.colorError : 'inherit',
            }}
          >
            {d.batch_volume.toFixed(2)}
          </span>
        ),
        structural_elements: d.delivery_note_data[0].structural_element,
        min_mashes_compliance: renderMinMashesCompliance(d),
        resistence_compliance: getResistenceCompilance(d),
        NC:
          d.non_conformity !== null
            ? intl.formatMessage({ id: 'yes' }).toUpperCase()
            : undefined,
        rawData: d,
      }));
    },
  };

  useLayoutEffect(() => {
    if (!concreteClassParam) {
      navigate('../');
    }
  }, [navigate, concreteClassParam]);

  useLayoutEffect(() => {
    const findConcreteClass = async () => {
      const data = await getConcreteClassesStatistical(id);
      if (!data) {
        navigate('../');
      } else {
        const cc = data.data.data.find((c) => c.name === concreteClassParam);
        if (cc) {
          setConcreteClass(cc);
          getLotificationList(cc.concrete_batch_data);
        } else {
          navigate('../');
        }
      }
    };

    findConcreteClass();
  }, [concreteClassParam, id, navigate, getLotificationList]);

  return (
    <>
      <div className={`row ${actionsWrapper}`}>
        <div className="col-sm-12">
          <div className={`row ${actionsWrapper}`}>
            <div className="col-sm-12">
              <Flex justify="flex-end">
                <AccionaButton
                  id="traceability"
                  style={{ padding: 15 }}
                  type="primary"
                  onClick={() => {
                    navigate('../traceability');
                  }}
                  msg={intl.formatMessage({ id: 'button.traceability' })}
                  classIcon="icon fas fa-route"
                />
                <AccionaButton
                  style={{ padding: 15 }}
                  id="100%Control"
                  type="primary"
                  onClick={() => {
                    navigate(`../100-control`);
                  }}
                  msg={intl.formatMessage({ id: 'button.100%Control' })}
                  classIcon="icon fas fa-percentage"
                />
                <AccionaButton
                  style={{ padding: 15 }}
                  id="statisticalControl"
                  type="primary"
                  onClick={() => {
                    navigate(`../statistical-control`);
                  }}
                  msg={intl.formatMessage({ id: 'button.statisticalControl' })}
                  classIcon="icon fas fa-chart-bar"
                />
              </Flex>
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <Collapse
            defaultActiveKey={['1']}
            expandIconPosition="end"
            expandIcon={({ isActive }) => (
              <DownOutlined rotate={isActive ? 180 : 0} />
            )}
            style={{ background: 'transparent', border: 'none' }}
            items={[
              {
                key: '1',
                label: concreteClass?.name
                  ? `${`${intl.formatMessage({
                      id: 'pages.quality.normative.statistical-control.lotting.list.title',
                    })} ${concreteClass?.name}`}`
                  : '',
                children: (
                  <Descriptions column={3} layout="vertical" colon={false}>
                    <Descriptions.Item
                      key="plant"
                      label={intl.formatMessage({
                        id: 'pages.quality.normative.statistical-control.lotting.list.plant',
                      })}
                    >
                      {concreteClass?.plant}
                    </Descriptions.Item>
                    <Descriptions.Item
                      key="recipe"
                      label={intl.formatMessage({
                        id: 'pages.quality.normative.statistical-control.lotting.list.recipe',
                      })}
                    >
                      {concreteClass?.recipe}
                    </Descriptions.Item>
                    <Descriptions.Item
                      key="control_type"
                      label={intl.formatMessage({
                        id: 'pages.quality.normative.statistical-control.lotting.list.control_type',
                      })}
                    >
                      {concreteClass?.control_type}
                    </Descriptions.Item>
                  </Descriptions>
                ),
                style: {
                  fontSize: '1.1em',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                },
              },
            ]}
          />
          <Divider />
          {casesList?.case0.batches.length > 0 && (
            <div style={{ marginTop: '3em' }}>
              <UncontrolledAccionaTable
                pageSize={casesList?.case0.batches.length}
                total={casesList?.case0.batches.length}
                title={
                  <Flex
                    justify="space-between"
                    style={{ width: '100%', padding: '0 1em' }}
                  >
                    <span>
                      {intl.formatMessage({
                        id: 'pages.quality.normative.statistical-control.lotting.list.case0',
                        defaultMessage: 'CASO 0',
                      })}
                    </span>
                    <span>
                      {intl.formatMessage(
                        {
                          id: 'pages.quality.normative.statistical-control.lotting.list.totalBatches',
                        },
                        { total: casesList.case0.totalBatches || 0 },
                      )}
                    </span>
                  </Flex>
                }
                items={casesList.case0.batches}
                actions={(item) => actions(item, 0)}
                {...commonProps}
              />
            </div>
          )}
          {casesList?.case1.batches.length > 0 && (
            <div style={{ marginTop: '3em' }}>
              <UncontrolledAccionaTable
                pageSize={casesList?.case1.batches.length}
                total={casesList?.case1.batches.length}
                title={
                  <Flex
                    justify="space-between"
                    style={{ width: '100%', padding: '0 1em' }}
                  >
                    <span>
                      {intl.formatMessage({
                        id: 'pages.quality.normative.statistical-control.lotting.list.case1',
                        defaultMessage: 'CASO 1',
                      })}
                    </span>
                    <span>
                      {intl.formatMessage(
                        {
                          id: 'pages.quality.normative.statistical-control.lotting.list.totalBatches',
                        },
                        { total: casesList.case1.totalBatches || 0 },
                      )}
                    </span>
                  </Flex>
                }
                items={casesList.case1.batches}
                actions={(item) => actions(item, 1)}
                {...commonProps}
              />
            </div>
          )}
          {normative === 'ehe' ? (
            <div style={{ marginTop: '3em' }}>
              {/* EHE: Caso 2 primero (1-36), luego Caso 3 (37+) */}
              {casesList?.case2.batches.length > 0 && (
                <div>
                  <UncontrolledAccionaTable
                    pageSize={casesList?.case2.batches.length}
                    total={casesList?.case2.batches.length}
                    title={
                      <Flex
                        justify="space-between"
                        style={{ width: '100%', padding: '0 1em' }}
                      >
                        <span>
                          {intl.formatMessage({
                            id: 'pages.quality.normative.statistical-control.lotting.list.case2-1-36',
                            defaultMessage: 'CASO 2 [Albaranes 1-36]',
                          })}
                        </span>
                        <span>
                          {intl.formatMessage(
                            {
                              id: 'pages.quality.normative.statistical-control.lotting.list.totalBatches',
                            },
                            { total: casesList.case2.totalBatches || 0 },
                          )}
                        </span>
                      </Flex>
                    }
                    items={casesList.case2.batches}
                    actions={(item) => actions(item, 2)}
                    {...commonProps}
                  />
                  {casesList.case2.error && casesList.case3.error && (
                    <Alert
                      message={intl.formatMessage({
                        id: 'pages.quality.normative.statistical-control.lotting.list.alert.case23Error',
                      })}
                      type="error"
                      showIcon
                      style={{ marginTop: '1em', marginBottom: '3em' }}
                    />
                  )}
                  {casesList.case2.error && !casesList.case3.error && (
                    <Alert
                      message={intl.formatMessage({
                        id: 'pages.quality.normative.statistical-control.lotting.list.alert.case2Error',
                      })}
                      type="error"
                      showIcon
                      style={{ marginTop: '1em', marginBottom: '3em' }}
                    />
                  )}
                </div>
              )}
              {casesList?.case3.batches.length > 0 && (
                <div style={{ marginTop: '3em' }}>
                  <UncontrolledAccionaTable
                    pageSize={casesList?.case3.batches.length}
                    total={casesList?.case3.batches.length}
                    title={
                      <Flex
                        justify="space-between"
                        style={{ width: '100%', padding: '0 1em' }}
                      >
                        <span>
                          {intl.formatMessage({
                            id: 'pages.quality.normative.statistical-control.lotting.list.case3-37+',
                            defaultMessage: 'CASO 3 [Albaranes 37+]',
                          })}
                        </span>
                        <span>
                          {intl.formatMessage(
                            {
                              id: 'pages.quality.normative.statistical-control.lotting.list.totalBatches',
                            },
                            { total: casesList.case3.totalBatches || 0 },
                          )}
                        </span>
                      </Flex>
                    }
                    items={casesList.case3.batches}
                    actions={(item) => actions(item, 3)}
                    {...commonProps}
                  />
                  {casesList.case3.error && !casesList.case2.error && (
                    <Alert
                      message={intl.formatMessage({
                        id: 'pages.quality.normative.statistical-control.lotting.list.alert.case3Error',
                      })}
                      type="error"
                      showIcon
                      style={{ marginTop: '1em', marginBottom: '3em' }}
                    />
                  )}
                </div>
              )}
            </div>
          ) : (
            <div>
              {/* COE: Caso 3 primero (1-36), luego Caso 2 (37+) */}
              {casesList?.case3.batches.length > 0 && (
                <div marginTop="3em">
                  <UncontrolledAccionaTable
                    pageSize={casesList?.case3.batches.length}
                    total={casesList?.case3.batches.length}
                    title={
                      <Flex
                        justify="space-between"
                        style={{ width: '100%', padding: '0 1em' }}
                      >
                        <span>
                          {intl.formatMessage({
                            id: 'pages.quality.normative.statistical-control.lotting.list.case3-1-36',
                            defaultMessage: 'CASO 3 [Albaranes 1-36]',
                          })}
                        </span>
                        <span>
                          {intl.formatMessage(
                            {
                              id: 'pages.quality.normative.statistical-control.lotting.list.totalBatches',
                            },
                            { total: casesList.case3.totalBatches || 0 },
                          )}
                        </span>
                      </Flex>
                    }
                    items={casesList.case3.batches}
                    actions={(item) => actions(item, 3)}
                    {...commonProps}
                  />
                  {casesList.case3.error && casesList.case2.error && (
                    <Alert
                      message={intl.formatMessage({
                        id: 'pages.quality.normative.statistical-control.lotting.list.alert.case23Error',
                      })}
                      type="error"
                      showIcon
                      style={{ marginTop: '1em', marginBottom: '3em' }}
                    />
                  )}
                  {casesList.case3.error && !casesList.case2.error && (
                    <Alert
                      message={intl.formatMessage({
                        id: 'pages.quality.normative.statistical-control.lotting.list.alert.case3Error',
                      })}
                      type="error"
                      showIcon
                      style={{ marginTop: '1em', marginBottom: '3em' }}
                    />
                  )}
                </div>
              )}
              {casesList?.case2.batches.length > 0 && (
                <div style={{ marginTop: '3em' }}>
                  <UncontrolledAccionaTable
                    pageSize={casesList?.case2.batches.length}
                    total={casesList?.case2.batches.length}
                    title={
                      <Flex
                        justify="space-between"
                        style={{ width: '100%', padding: '0 1em' }}
                      >
                        <span>
                          {intl.formatMessage({
                            id: 'pages.quality.normative.statistical-control.lotting.list.case2-37+',
                            defaultMessage: 'CASO 2 [Albaranes 37+]',
                          })}
                        </span>
                        <span>
                          {intl.formatMessage(
                            {
                              id: 'pages.quality.normative.statistical-control.lotting.list.totalBatches',
                            },
                            { total: casesList.case2.totalBatches || 0 },
                          )}
                        </span>
                      </Flex>
                    }
                    items={casesList.case2.batches}
                    actions={(item) => actions(item, 2)}
                    {...commonProps}
                  />
                  {casesList.case2.error && !casesList.case3.error && (
                    <Alert
                      message={intl.formatMessage({
                        id: 'pages.quality.normative.statistical-control.lotting.list.alert.case2Error',
                      })}
                      type="error"
                      showIcon
                      style={{ marginTop: '1em', marginBottom: '3em' }}
                    />
                  )}
                </div>
              )}
            </div>
          )}
          {casesList &&
            casesList.case0.batches.length === 0 &&
            casesList.case1.batches.length === 0 &&
            casesList.case2.batches.length === 0 &&
            casesList.case3.batches.length === 0 && (
              <div
                style={{
                  backgroundColor: 'white',
                  padding: '2em',
                  textAlign: 'center',
                }}
              >
                <h4>
                  {intl.formatMessage({
                    id: 'pages.quality.normative.statistical-control.lotting.list.noData',
                  })}
                </h4>
              </div>
            )}
        </div>
      </div>
    </>
  );
};

CustomLottingList.propTypes = {
  normative: PropTypes.oneOf(['ehe', 'coe']).isRequired,
};

CustomLottingList.defaultProps = {
  normative: 'ehe',
};

export default CustomLottingList;
